/**
 * Light and dark theme.
 *
 * The choice is kept per browser in localStorage ('light' | 'dark' |
 * 'system') and applied as <html data-theme> (our tokens, Tailwind) and
 * <html data-bs-theme> (Bootstrap's own dark mode). public/index.html applies
 * it before React loads, so a dark page never flashes white first.
 *
 * Light is the default until the user picks: some older screens still carry
 * hard-coded colours, so dark is opt-in rather than following the OS.
 */
const KEY = 'cx-theme';
const EVENT = 'cx-theme-change';

export const getThemePreference = () => {
  try {
    const saved = localStorage.getItem(KEY);
    return ['light', 'dark', 'system'].includes(saved) ? saved : 'light';
  } catch {
    return 'light';
  }
};

const systemPrefersDark = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches;

export const resolveTheme = (preference = getThemePreference()) =>
  (preference === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : preference);

export const applyTheme = (preference = getThemePreference()) => {
  const theme = resolveTheme(preference);
  const root = document.documentElement;
  root.setAttribute('data-theme', theme);
  root.setAttribute('data-bs-theme', theme);
  window.dispatchEvent(new CustomEvent(EVENT, { detail: theme }));
  return theme;
};

export const setThemePreference = (preference) => {
  try {
    localStorage.setItem(KEY, preference);
  } catch {
    // Private mode or blocked storage: the choice lasts for this page only
  }
  return applyTheme(preference);
};

// Follow the OS live when the preference is 'system'.
if (typeof window !== 'undefined' && window.matchMedia) {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener?.('change', () => {
    if (getThemePreference() === 'system') applyTheme('system');
  });
}

export const THEME_EVENT = EVENT;
